import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  TimePicker,
  Select,
  Tag,
  Badge,
  Space,
  Tooltip,
  message,
  Drawer,
  List,
  Avatar,
  Popconfirm,
  Row,
  Col,
  Statistic,
  InputNumber,
  Tabs,
} from 'antd';
import DataTable from '@/components/DataTable';
import PageHeader, { EmptyState } from '@/components/PageHeader';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import Axios from 'axios';
import moment from 'moment';
import ShiftAssignmentValidation from '../../../components/ShiftAssignmentValidation';
import StatCard from '@/components/StatCard';
import { Clock, CheckCircle, Users, AlertTriangle } from 'lucide-react';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import PageModal from '@/components/PageModal';
import HeaderActions from '@/components/HeaderActions';

const { Option } = Select;
const { TextArea } = Input;

const ShiftManagement = ({ isEmbedded = false, active = true }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [shifts, setShifts] = useState([]);
  const [personnelTypes, setPersonnelTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shiftModalVisible, setShiftModalVisible] = useState(false);
  const [personnelModalVisible, setPersonnelModalVisible] = useState(false);
  const [rosterDrawerVisible, setRosterDrawerVisible] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftRoster, setShiftRoster] = useState([]);
  const [availablePersonnel, setAvailablePersonnel] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [conflictModalVisible, setConflictModalVisible] = useState(false);
  const [assignmentConflicts, setAssignmentConflicts] = useState([]);

  const [shiftForm] = Form.useForm();
  const [personnelForm] = Form.useForm();

  const token = localStorage.getItem('Token');

  // Fetch all data
  useEffect(() => {
    fetchShifts();
    fetchPersonnelTypes();
  }, []);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const response = await Axios.get('/bp/shifts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setShifts(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch shifts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonnelTypes = async () => {
    try {
      const response = await Axios.get('/bp/personnel-types', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setPersonnelTypes(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch personnel types');
      console.error(error);
    }
  };

  const fetchShiftRoster = async (shiftId) => {
    setLoading(true);
    try {
      const response = await Axios.get(`/bp/shift-assignments/roster/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setShiftRoster(response.data.roster);
      }
    } catch (error) {
      message.error('Failed to fetch shift roster');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePersonnel = async (shiftId, search = '') => {
    try {
      const response = await Axios.get('/bp/shift-assignments/available-personnel', {
        params: { shift_id: shiftId, search },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        setAvailablePersonnel(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch available personnel');
      console.error(error);
    }
  };

  // Shift CRUD Operations
  const handleCreateShift = () => {
    setEditingShift(null);
    shiftForm.resetFields();
    setShiftModalVisible(true);
  };

  const handleEditShift = (shift) => {
    setEditingShift(shift);
    shiftForm.setFieldsValue({
      shift_name: shift.shift_name,
      shift_code: shift.shift_code,
      start_time: moment(shift.start_time, 'HH:mm'),
      end_time: moment(shift.end_time, 'HH:mm'),
      shift_type: shift.shift_type,
      description: shift.description,
      min_capacity: shift.min_capacity,
      max_capacity: shift.max_capacity,
    });
    setShiftModalVisible(true);
  };

  const handleDeleteShift = async (shiftId) => {
    setLoading(true);
    try {
      const response = await Axios.delete(`/bp/shifts/${shiftId}/delete`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === 'success') {
        message.success('Shift deactivated successfully');
        fetchShifts();
      }
    } catch (error) {
      message.error('Failed to deactivate shift');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleShiftSubmit = async (values) => {
    setLoading(true);
    try {
      const data = {
        shift_name: values.shift_name,
        shift_code: values.shift_code,
        start_time: values.start_time.format('HH:mm'),
        end_time: values.end_time.format('HH:mm'),
        shift_type: values.shift_type,
        description: values.description,
        min_capacity: values.min_capacity || 0,
        max_capacity: values.max_capacity,
      };

      let response;
      if (editingShift) {
        response = await Axios.put(`/bp/shifts/${editingShift.id}/update`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await Axios.post('/bp/shifts/create', data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.data.status === 'success') {
        message.success(editingShift ? 'Shift updated successfully' : 'Shift created successfully');
        setShiftModalVisible(false);
        shiftForm.resetFields();
        fetchShifts();
      }
    } catch (error) {
      message.error('Failed to save shift');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Personnel Assignment Operations
  const handleAssignPersonnel = (shift) => {
    setSelectedShift(shift);
    fetchAvailablePersonnel(shift.id);
    personnelForm.resetFields();
    setPersonnelModalVisible(true);
  };

  const handlePersonnelAssignmentSubmit = async (values) => {
    setLoading(true);
    try {
      const data = {
        shift_id: selectedShift.id,
        employee_id: values.employee_id,
        personnel_type_id: values.personnel_type_id,
        is_primary: values.is_primary || false,
        notes: values.notes || '',
      };

      const response = await Axios.post('/bp/shift-assignments/assign', data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'success') {
        message.success('Personnel assigned successfully');
        setPersonnelModalVisible(false);
        personnelForm.resetFields();
        fetchShifts();
        if (rosterDrawerVisible) {
          fetchShiftRoster(selectedShift.id);
        }
      } else if (response.data.status === 'error' && response.data.errors) {
        // Handle conflict errors
        setAssignmentConflicts(response.data.errors);
        setConflictModalVisible(true);
        setPersonnelModalVisible(false);
      }
    } catch (error) {
      // Check if error response contains conflict information
      if (error.response?.data?.status === 'error' && error.response?.data?.errors) {
        setAssignmentConflicts(error.response.data.errors);
        setConflictModalVisible(true);
        setPersonnelModalVisible(false);
      } else {
        message.error(error.response?.data?.message || 'Failed to assign personnel');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePersonnel = async (assignmentId) => {
    setLoading(true);
    try {
      const response = await Axios.delete(`/bp/shift-assignments/${assignmentId}/remove`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'success') {
        message.success('Personnel removed successfully');
        fetchShiftRoster(selectedShift.id);
        fetchShifts();
      }
    } catch (error) {
      message.error('Failed to remove personnel');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRoster = (shift) => {
    setSelectedShift(shift);
    fetchShiftRoster(shift.id);
    setRosterDrawerVisible(true);
  };

  // Table columns
  const allColumns = [
    {
      title: 'Shift Name',
      dataIndex: 'shift_name',
      key: 'shift_name',
      width: 240,
      defaultVisible: true,
      sortType: 'string',
      render: (text, record) => (
        <Space>
          <ClockCircleOutlined />
          <strong>{text || `${record.start_time} - ${record.end_time}`}</strong>
        </Space>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'shift_code',
      key: 'shift_code',
      width: 150,
      defaultVisible: true,
      sortType: 'string',
      render: (code) => code && <Tag>{code}</Tag>,
    },
    {
      title: 'Time',
      key: 'time',
      width: 160,
      defaultVisible: true,
      render: (_, record) => (
        <span>
          {record.start_time} - {record.end_time}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'shift_type',
      key: 'shift_type',
      width: 130,
      defaultVisible: true,
      sortType: 'string',
      render: (type) => {
        const colors = {
          DAY: 'gold',
          NIGHT: 'purple',
          ROTATING: 'default',
          FLEXIBLE: 'green',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Personnel',
      key: 'personnel',
      width: 140,
      defaultVisible: true,
      sortType: 'number',
      render: (_, record) => {
        const percentage = record.max_capacity
          ? (record.current_personnel / record.max_capacity) * 100
          : 0;
        const status = percentage >= 100 ? 'error' : percentage >= 80 ? 'warning' : 'success';

        return (
          <Space>
            <Badge
              count={record.current_personnel}
              style={{ backgroundColor: status === 'error' ? '#ff4d4f' : '#52c41a' }}
            />
            {record.max_capacity && (
              <span style={{ fontSize: '12px', color: '#999' }}>/ {record.max_capacity}</span>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Capacity',
      key: 'capacity',
      width: 150,
      defaultVisible: true,
      sortType: 'number',
      render: (_, record) => {
        if (!record.max_capacity) return '-';
        const percentage = (record.current_personnel / record.max_capacity) * 100;
        return (
          <Tooltip title={`${record.current_personnel} / ${record.max_capacity} assigned`}>
            <div style={{ width: '100px' }}>
              <div
                style={{
                  height: '8px',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(percentage, 100)}%`,
                    backgroundColor:
                      percentage >= 100 ? '#ff4d4f' : percentage >= 80 ? '#faad14' : '#52c41a',
                    transition: 'width 0.3s',
                  }}
                />
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      defaultVisible: true,
      render: (isActive) =>
        isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Active
          </Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 190,
      fixed: 'right',
      defaultVisible: true,
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="View Roster">
            <Button size="small" aria-label={`View roster of ${record.shift_name}`} icon={<TeamOutlined style={{ color: defaultColors.primary }} />} onClick={() => handleViewRoster(record)} />
          </Tooltip>
          <Tooltip title="Assign Personnel">
            <Button size="small" aria-label={`Assign personnel to ${record.shift_name}`} icon={<UserAddOutlined />} onClick={() => handleAssignPersonnel(record)} />
          </Tooltip>
          <Tooltip title="Edit Shift">
            <Button size="small" aria-label={`Edit ${record.shift_name}`} icon={<Icons.Pencil className="w-4 h-4 text-amber-600" />} onClick={() => handleEditShift(record)} />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to deactivate this shift?"
            onConfirm={() => handleDeleteShift(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Deactivate Shift">
              <Button size="small" danger aria-label={`Deactivate ${record.shift_name}`} icon={<Icons.Trash2 className="w-4 h-4" />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const addShiftButton = (
    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateShift}>
      Create Shift
    </Button>
  );

  const content = (
    <>
        {/* Top Summary Cards */}
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}>
            <StatCard
              label="Total Shifts"
              value={shifts.length}
              icon={<Clock size={24} />}
              bgColor={`${defaultColors.primary}22`}
              iconColor={defaultColors.primary}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatCard
              label="Active Shifts"
              value={shifts.filter((s) => s.is_active).length}
              icon={<CheckCircle size={24} />}
              bgColor={`${defaultColors.success}22`}
              iconColor={defaultColors.success}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatCard
              label="Total Personnel"
              value={shifts.reduce((sum, shift) => sum + (shift.current_personnel || 0), 0)}
              icon={<Users size={24} />}
              bgColor={`${defaultColors.info}22`}
              iconColor={defaultColors.info}
            />
          </Col>
          <Col xs={12} md={6}>
            <StatCard
              label="Capacity Alerts"
              value={
                shifts.filter((s) => s.max_capacity && s.current_personnel >= s.max_capacity).length
              }
              icon={<AlertTriangle size={24} />}
              bgColor={`${defaultColors.error}22`}
              iconColor={defaultColors.error}
            />
          </Col>
        </Row>

        {/* Shifts Table */}
          <DataTable
            className="shift-management-table"
            tableKey="shift_config"
            searchable
            searchPlaceholder="Search shifts..."
            columnConfig
            showCount={false}
            exportable
            exportFilename="shifts"
            columns={allColumns}
            data={shifts}
            rowKey="id"
            loading={loading}
            locale={{
              emptyText: (
                <EmptyState
                  title="No shifts yet"
                  description="Create your first shift to start assigning personnel."
                  actionLabel="Create Shift"
                  onAction={handleCreateShift}
                />
              ),
            }}
            bordered
            scrollX="max-content"
            height={null}
          />
    </>
  );

  return (
    <div
      className={isEmbedded ? '' : 'p-2.5 pt-5'}
      style={isEmbedded ? undefined : { backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      {isEmbedded ? (
        <>
          {/* hosted in Shifts: Create Shift goes in that page's header, only while this tab is open */}
          {active && <HeaderActions>{addShiftButton}</HeaderActions>}
          {content}
        </>
      ) : (
        <Card
          className="border border-gray-300 shadow-md"
          bordered={false}
          classNames={{ body: 'page-card-scroll' }}
          style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff', height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
          styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
          title={
            <PageHeader
              compact
              icon={<Icons.Clock size={22} />}
              title={t('Shift Management')}
              description="Define working shifts, capacity and personnel assignments."
              extra={addShiftButton}
            />
          }
        >
          {content}
        </Card>
      )}

      {/* Shift Create/Edit Modal */}
      <PageModal
        open={shiftModalVisible}
        icon={<ClockCircleOutlined style={{ fontSize: 22 }} />}
        title={editingShift ? 'Edit Shift' : 'Create New Shift'}
        description="A working shift with its times and personnel capacity."
        onCancel={() => {
          setShiftModalVisible(false);
          shiftForm.resetFields();
        }}
        width={600}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setShiftModalVisible(false); shiftForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" loading={loading} onClick={() => shiftForm.submit()}>{editingShift ? 'Update' : 'Create'}</Button>
          </div>
        }
      >
        <Form form={shiftForm} layout="vertical" onFinish={handleShiftSubmit}>
          <Form.Item
            label="Shift Name"
            name="shift_name"
            rules={[{ required: true, message: 'Please enter shift name' }]}
          >
            <Input placeholder="e.g., Morning Shift, Night Shift" />
          </Form.Item>

          <Form.Item
            label="Shift Code"
            name="shift_code"
            rules={[{ required: true, message: 'Please enter shift code' }]}
          >
            <Input placeholder="e.g., SHIFT_A, MORNING" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Start Time"
                name="start_time"
                rules={[{ required: true, message: 'Please select start time' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="End Time"
                name="end_time"
                rules={[{ required: true, message: 'Please select end time' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Shift Type"
            name="shift_type"
            rules={[{ required: true, message: 'Please select shift type' }]}
          >
            <Select placeholder="Select shift type">
              <Option value="DAY">Day Shift</Option>
              <Option value="NIGHT">Night Shift</Option>
              <Option value="ROTATING">Rotating Shift</Option>
              <Option value="FLEXIBLE">Flexible</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Minimum Capacity" name="min_capacity">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Min personnel" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Maximum Capacity" name="max_capacity">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Max personnel" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <TextArea rows={3} placeholder="Shift description and notes" />
          </Form.Item>
        </Form>
      </PageModal>

      {/* Assign Personnel Modal */}
      <PageModal
        open={personnelModalVisible}
        icon={<UserAddOutlined style={{ fontSize: 22 }} />}
        title="Assign Personnel"
        description={selectedShift?.shift_name || 'Shift'}
        onCancel={() => {
          setPersonnelModalVisible(false);
          personnelForm.resetFields();
        }}
        width={500}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setPersonnelModalVisible(false); personnelForm.resetFields(); }}>Cancel</Button>
            <Button type="primary" loading={loading} onClick={() => personnelForm.submit()}>Assign</Button>
          </div>
        }
      >
        <Form form={personnelForm} layout="vertical" onFinish={handlePersonnelAssignmentSubmit}>
          <Form.Item
            label="Employee"
            name="employee_id"
            rules={[{ required: true, message: 'Please select an employee' }]}
          >
            <Select
              showSearch
              placeholder="Search and select employee"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onSearch={(value) => fetchAvailablePersonnel(selectedShift?.id, value)}
            >
              {availablePersonnel.map((person) => (
                <Option key={person.id} value={person.id} disabled={person.is_assigned}>
                  {person.name} ({person.employee_unique_id})
                  {person.is_assigned && ' - Already Assigned'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Personnel Type/Role"
            name="personnel_type_id"
            rules={[{ required: true, message: 'Please select personnel type' }]}
          >
            <Select placeholder="Select role">
              {personnelTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  <Tag color={type.color}>{type.name}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_primary" valuePropName="checked">
            <Select placeholder="Is Primary?">
              <Option value={true}>Primary</Option>
              <Option value={false}>Backup/Support</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea rows={2} placeholder="Assignment notes (optional)" />
          </Form.Item>
        </Form>
      </PageModal>

      {/* Roster Drawer */}
      <Drawer
        title={
          <Space>
            <TeamOutlined />
            <span>{selectedShift?.shift_name || 'Shift'} Roster</span>
            {selectedShift && (
              <Tag>
                {selectedShift.start_time} - {selectedShift.end_time}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        width={600}
        classNames={{ body: 'page-card-scroll' }}
        open={rosterDrawerVisible}
        onClose={() => setRosterDrawerVisible(false)}
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              setRosterDrawerVisible(false);
              handleAssignPersonnel(selectedShift);
            }}
          >
            Add Personnel
          </Button>
        }
      >
        <List
          loading={loading}
          dataSource={shiftRoster}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Popconfirm
                  title="Remove this person from the shift?"
                  onConfirm={() => handleRemovePersonnel(item.assignment_id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="text" danger size="small">
                    Remove
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar style={{ backgroundColor: item.personnel_type_color }}>
                    {item.employee_name.charAt(0)}
                  </Avatar>
                }
                title={
                  <Space>
                    <span>{item.employee_name}</span>
                    {item.is_primary && <Tag color="gold">Primary</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    <Tag color={item.personnel_type_color}>{item.personnel_type_name}</Tag>
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      ID: {item.employee_unique_id}
                    </span>
                    {item.notes && <span style={{ fontSize: '12px' }}>Note: {item.notes}</span>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>

      {/* Conflict Validation Modal */}
      <ShiftAssignmentValidation
        visible={conflictModalVisible}
        conflicts={assignmentConflicts}
        onClose={() => {
          setConflictModalVisible(false);
          setAssignmentConflicts([]);
        }}
      />
    </div>
  );
};

export default ShiftManagement;
